import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Typography, 
  Space, 
  Modal, 
  Form, 
  InputNumber, 
  message,
  Table,
  Tag,
  Tooltip
} from 'antd';
import { 
  DollarCircleOutlined,
  EditOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api';

const { Title, Text } = Typography;

export interface ActionCost {
  action: string;
  cost: number;
  description: string;
  currency: string;
}

interface ActionCostsResponse {
  success: boolean;
  currency: string;
  costs: ActionCost[];
}

const ActionCosts: React.FC = () => {
  const [editingAction, setEditingAction] = useState<ActionCost | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch action costs
  const { data: actionCostsData, isLoading } = useQuery<ActionCostsResponse>({
    queryKey: ['action-costs'],
    queryFn: async () => {
      const response = await adminApi.getActionCosts();
      return response;
    },
  });

  // Update mutation
  const updateCostMutation = useMutation({
    mutationFn: ({ action, cost }: { action: string; cost: number }) => {
      return adminApi.updateActionCost(action, cost);
    },
    onSuccess: () => {
      message.success('Action cost updated successfully');
      queryClient.invalidateQueries({ queryKey: ['action-costs'] });
      setModalVisible(false);
      setEditingAction(null);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error('Failed to update action cost: ' + (error.response?.data?.detail || error.message || 'Unknown error'));
    }
  });

  const handleEdit = (actionCost: ActionCost) => {
    setEditingAction(actionCost);
    setModalVisible(true);
    form.setFieldsValue({
      cost: actionCost.cost,
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingAction) {
        updateCostMutation.mutate({
          action: editingAction.action,
          cost: values.cost,
        });
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const formatActionName = (action: string) => {
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getActionColor = (action: string) => {
    const colors: Record<string, string> = {
      tryoff: 'blue',
      tryon: 'purple',
      quick_recommendations: 'green',
      chat_message: 'orange',
    };
    return colors[action] || 'default';
  };

  const columns = [
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action: string, record: ActionCost) => (
        <Space>
          <Tag color={getActionColor(action)} style={{ fontSize: '12px', padding: '4px 8px' }}>
            {formatActionName(action)}
          </Tag>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.description}
          </Text>
        </Space>
      ),
      sorter: (a: ActionCost, b: ActionCost) => a.action.localeCompare(b.action),
    },
    {
      title: 'Cost (ZAR)',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => (
        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
          {cost.toLocaleString()} ZAR
        </Text>
      ),
      sorter: (a: ActionCost, b: ActionCost) => a.cost - b.cost,
      width: 150,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 100,
      render: (_: any, record: ActionCost) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  const costs = actionCostsData?.costs || [];
  const currency = actionCostsData?.currency || 'ZAR';

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                <DollarCircleOutlined style={{ marginRight: '8px' }} />
                Action Costs
              </Title>
              <Text type="secondary">Manage ZAR costs for user actions (tryon, smart_add_cloth_item, quick_recom, chat_message, ...)</Text>
            </div>
          </Space>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['action-costs'] })}
            loading={isLoading}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={costs}
          loading={isLoading}
          rowKey="action"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} actions`,
          }}
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title={`Edit Cost: ${editingAction ? formatActionName(editingAction.action) : ''}`}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          form.resetFields();
          setModalVisible(false);
          setEditingAction(null);
        }}
        confirmLoading={updateCostMutation.isPending}
        width={500}
        okText="Save"
        cancelText="Cancel"
      >
        {editingAction && (
          <div style={{ marginBottom: '16px' }}>
            <Text type="secondary">{editingAction.description}</Text>
          </div>
        )}
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="Cost (ZAR)"
            name="cost"
            rules={[
              { required: true, message: 'Please enter cost' }, 
              { type: 'number', min: 0, message: 'Cost must be non-negative' }
            ]}
            help="Cost in ZAR (integer, no decimals)"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={1}
              precision={0}
              placeholder="0"
              addonAfter={currency}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ActionCosts;

